import styled from 'styled-components'
import { ProductQuantityQuickAdd } from '@/features/shop/components/ProductQuantityQuickAdd'
import { SkeletonConsumer } from '@/features/shared/components/SkeletonLoader'
import { type PromotionMetadata } from '@/features/shop/services/Product/types'
import {
  type ProductCardProduct,
  type OnQtyCallback,
  type OnProductCallback,
  type OnComplementaryShelfFetch,
} from '@/features/shop/components/ProductCard/types'
import { type UpdateProductQuantityFn } from '@/features/shared/hooks/useUpdateProductQuantity'
import { screenSizes } from '@shipt/design-system-themes'
import { useProductCardQuickAdd } from '@/features/shop/components/ProductCard/useProductCardQuickAdd'

type Props = {
  product: ProductCardProduct
  index: number
  className?: string
  onQtyAdded: OnQtyCallback
  onQtyRemoved: OnProductCallback
  showProductFlag: boolean
  shouldShowPopover?: boolean
  displayContext?: boolean
  showLargerProducts?: boolean
  productQuantity: number
  updateProductQuantity: UpdateProductQuantityFn
  onComplementaryShelfFetch?: OnComplementaryShelfFetch
  updateListOnly?: boolean
  hideDeleteIcon?: boolean
  promotionMetadata?: PromotionMetadata | null
}

export function ProductCardQuickAdd({
  product,
  index = 0,
  className,
  onQtyAdded,
  onQtyRemoved,
  shouldShowPopover,
  showProductFlag,
  displayContext,
  showLargerProducts = false,
  productQuantity,
  updateProductQuantity,
  onComplementaryShelfFetch,
  updateListOnly,
  hideDeleteIcon,
  promotionMetadata,
}: Readonly<Props>) {
  const { handleAddQty, handleRemoveQty, isUpdatingQuantity } = useProductCardQuickAdd({
    index,
    product,
    onQtyAdded,
    onQtyRemoved,
    productQuantity,
    updateProductQuantity,
  })

  if (product.oos) return null

  return (
    <SkeletonConsumer width={0} height={0}>
      <QuickAddContainer className={className} data-show-larger-products={showLargerProducts}>
        <ProductQuantityQuickAdd
          quantity={productQuantity}
          product={product}
          onAdd={handleAddQty}
          onRemove={handleRemoveQty}
          showProductFlag={showProductFlag}
          shouldShowPopover={Boolean(shouldShowPopover)}
          displayContext={displayContext}
          isUpdatingQuantity={isUpdatingQuantity}
          index={index}
          showLargerProducts={showLargerProducts}
          onComplementaryShelfFetch={onComplementaryShelfFetch}
          updateListOnly={updateListOnly}
          hideDeleteIcon={hideDeleteIcon}
          promotionMetadata={promotionMetadata}
        />
      </QuickAddContainer>
    </SkeletonConsumer>
  )
}

export const QuickAddContainer = styled.div<{
  'data-show-larger-products'?: boolean
}>`
  position: absolute;
  z-index: 3;
  top: 6.75rem;
  right: 0.5rem;
  width: calc(100% - 16px);

  @media ${screenSizes.tablet} {
    &[data-show-larger-products='true'] {
      top: 10rem;
    }
  }
`
